import React from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Divider,
    alpha
} from '@mui/material';
import {
    Security as Shield,
    Visibility as Eye,
    Group as Users,
    Lock as Lock,
    Phone as Phone,
    Email as Mail,
    LocationOn as MapPin,
    FiberManualRecord as Circle,
    MedicalServices as Medical,
    Payment as Payment,
    Gavel as Legal,
    Analytics as Analytics,
    Chat as Chat
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';

// Create a custom theme with the blue/indigo color scheme
const theme = createTheme({
    palette: {
        primary: {
            main: '#1976d2', // blue
            light: '#42a5f5',
            dark: '#1565c0',
        },
        secondary: {
            main: '#7b1fa2', // indigo/purple
            light: '#ba68c8',
            dark: '#6a1b9a',
        },
        background: {
            default: 'linear-gradient(to bottom right, #e3f2fd, #ffffff, #ede7f6)',
        },
    },
    typography: {
        h4: {
            fontWeight: 700,
        },
        h5: {
            fontWeight: 600,
        },
        h6: {
            fontWeight: 600,
        },
    },
    components: {
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                },
            },
        },
    },
});

const PrivacyPolicy = () => {



    return (
        <ThemeProvider theme={theme}>
            <Box
                sx={{
                    minHeight: '100vh',
                    background: theme.palette.background.default
                }}
            >
                {/* Header Section */}
                <Box
                    sx={{
                        background: 'linear-gradient(to right, #1976d2, #7b1fa2)',
                        color: 'white',
                        py: 8
                    }}
                >
                    <Container maxWidth="md">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                            <Shield sx={{ fontSize: 120, color: alpha('#fff', 0.7) }} />
                            <Box>
                                <Typography variant="h3" fontWeight="bold" gutterBottom>
                                    Privacy Policy
                                </Typography>
                                <Typography variant="h6" sx={{ color: alpha('#fffdfdff', 0.9) }}>
                                    24Care Hub Center LLC
                                </Typography>
                            </Box>
                        </Box>
                        <Typography variant="body1" sx={{ color: alpha('#fff', 0.9), maxWidth: 'lg' }}>
                            We are committed to protecting your personal data in compliance with UAE data protection laws.
                        </Typography>
                    </Container>
                </Box>

                {/* Main Content */}
                <Container maxWidth="md" sx={{ py: 6 }}>

                    {/* Introduction */}
                    <Paper elevation={2} sx={{ p: 4, mb: 6, border: '1px solid', borderColor: 'grey.100' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                            <Shield sx={{ color: 'primary.main' }} />
                            <Typography variant="h5" component="h2">
                                Introduction
                            </Typography>
                        </Box>
                        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                            24Care Hub Center LLC ("we," "us," or "our") is committed to protecting your personal data
                            in compliance with UAE data protection laws. This Privacy Policy explains how we collect,
                            use, disclose, retain, and safeguard your personal and medical information when you visit
                            our website <a href="http://www.24care.ae" style={{ color: '#1976d2', textDecoration: 'underline' }}>www.24care.ae</a>,
                            make appointments, or use our healthcare services.
                        </Typography>
                    </Paper>

                    {/* Information We Collect */}
                    <Paper elevation={2} sx={{ p: 4, mb: 6, border: '1px solid', borderColor: 'grey.100' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 4 }}>
                            <Eye sx={{ color: 'primary.main' }} />
                            <Typography variant="h5" component="h2">
                                Information We Collect
                            </Typography>
                        </Box>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Card sx={{ bgcolor: 'primary.50', height: '100%' }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Personal Information You Provide
                                        </Typography>
                                        <List>
                                            {[
                                                "Identification & Contact: name, address, phone number, email",
                                                "Medical & Health-Related Data: medical history, current conditions, diagnosis, treatments, prescriptions, test results",
                                                "Payment & Billing: insurance details or payment card information"
                                            ].map((item, index) => (
                                                <ListItem key={index} alignItems="flex-start" sx={{ py: 0.5 }}>
                                                    <ListItemIcon sx={{ minWidth: 30, pt: 1 }}>
                                                        <Circle sx={{ fontSize: 10, color: 'primary.main' }} />
                                                    </ListItemIcon>
                                                    <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card sx={{ bgcolor: 'secondary.50', height: '100%' }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Automatically Collected Data
                                        </Typography>
                                        <List>
                                            {[
                                                "Usage Data: IP address, browser/device type, pages visited, timestamps",
                                                "Cookies & Tracking: to enhance performance, analytics, and personalize content"
                                            ].map((item, index) => (
                                                <ListItem key={index} alignItems="flex-start" sx={{ py: 0.5 }}>
                                                    <ListItemIcon sx={{ minWidth: 30, pt: 1 }}>
                                                        <Circle sx={{ fontSize: 10, color: 'secondary.main' }} />
                                                    </ListItemIcon>
                                                    <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* How We Use Your Information */}

                    <Paper elevation={2} sx={{ p: 4, mb: 6, border: '1px solid', borderColor: 'grey.100' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 4 }}>
                            <Users sx={{ color: 'primary.main' }} />
                            <Typography variant="h5" component="h2">
                                Sharing & Disclosure of Your Data
                            </Typography>
                        </Box>

                        <List>
                            {[
                                {
                                    title: "Healthcare Services",
                                    desc: "Share relevant medical data with doctors and caregivers involved in your treatment",
                                    color: "success.light"
                                },
                                {
                                    title: "Operations & Enhancements",
                                    desc: "Analyze usage for improving service quality and outcome",
                                    color: "primary.light"
                                },
                                {
                                    title: "Communication",
                                    desc: "Appointment reminders, follow-ups, updates, billing notifications",
                                    color: "secondary.light"
                                },
                                {
                                    title: "Payment Processing",
                                    desc: "Manage claims, invoicing, and any financial interactions",
                                    color: "warning.light"
                                },
                                {
                                    title: "Legal Compliance",
                                    desc: "Respond to lawful requests, fraud prevention, and risk management",
                                    color: "error.light"
                                }
                            ].map((item, index) => (
                                <React.Fragment key={index}>
                                    <ListItem alignItems="flex-start" sx={{ bgcolor: 'grey.50', borderRadius: 2, mb: 1, py: 2 }}>
                                        <ListItemIcon sx={{ minWidth: 30, pt: 1 }}>
                                            <Circle sx={{ fontSize: 10, color: 'primary.main' }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item.title}
                                            secondary={item.desc}
                                            primaryTypographyProps={{ fontWeight: 'medium' }}
                                        />
                                    </ListItem>
                                    {index < 3 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    </Paper>


                    {/* Data Sharing */}
                    <Paper elevation={2} sx={{ p: 4, mb: 6, border: '1px solid', borderColor: 'grey.100' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 4 }}>
                            <Lock sx={{ color: 'primary.main' }} />
                            <Typography variant="h5" component="h2">
                                Sharing & Disclosure of Your Data
                            </Typography>
                        </Box>

                        <List>
                            {[
                                { title: "Healthcare Professionals", desc: "Only those directly involved in your care" },
                                { title: "Service Providers", desc: "IT support, analytics, payment processors — all bound by confidentiality agreements" },
                                { title: "Legal or Regulatory Authorities", desc: "When required under UAE law or court order" },
                                { title: "Business Transfers", desc: "In case of restructuring or acquisition, with notice and protections in place" }
                            ].map((item, index) => (
                                <React.Fragment key={index}>
                                    <ListItem alignItems="flex-start" sx={{ bgcolor: 'grey.50', borderRadius: 2, mb: 1, py: 2 }}>
                                        <ListItemIcon sx={{ minWidth: 30, pt: 1 }}>
                                            <Circle sx={{ fontSize: 10, color: 'primary.main' }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item.title}
                                            secondary={item.desc}
                                            primaryTypographyProps={{ fontWeight: 'medium' }}
                                        />
                                    </ListItem>
                                    {index < 3 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    </Paper>

                    {/* Your Rights */}
                    <Paper
                        elevation={2}
                        sx={{
                            p: 4,
                            mb: 6,
                            background: 'linear-gradient(to right, #e3f2fd, #ede7f6)',
                            border: '1px solid',
                            borderColor: 'primary.100'
                        }}
                    >
                        <Typography variant="h5" component="h2" gutterBottom>
                            Your Rights
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            You may exercise the following rights under applicable data protection laws:
                        </Typography>

                        <Grid container spacing={2}>
                            {[
                                "Access your personal data",
                                "Request correction or completion of inaccurate data",
                                "Request deletion or restriction of processing",
                                "Object to particular uses of your information",
                                "Withdraw consent where consent is the basis for processing"
                            ].map((right, index) => (
                                <Grid item xs={12} md={6} key={index}>
                                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Circle sx={{ fontSize: 10, color: 'primary.main' }} />
                                        <Typography variant="body2">{right}</Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>

                    {/* Additional Sections */}
                    <Grid container spacing={4} sx={{ mb: 6 }}>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={2} sx={{ p: 4, height: '100%', border: '1px solid', borderColor: 'grey.100' }}>
                                <Typography variant="h6" gutterBottom>
                                    Data Security
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    We implement appropriate administrative, technical and physical safeguards to protect your data from
                                    unauthorized access, alteration, misuse or loss. However, no system is completely secure.
                                </Typography>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Paper elevation={2} sx={{ p: 4, height: '100%', border: '1px solid', borderColor: 'grey.100' }}>
                                <Typography variant="h6" gutterBottom>
                                    Data Retention
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    We retain personal data only as long as needed to provide services and meet legal obligations.
                                    Once no longer required, data will be securely deleted or anonymized.
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Contact Information */}
                    <Paper
                        elevation={2}
                        sx={{
                            p: 4,
                            background: 'linear-gradient(to right, #1976d2, #7b1fa2)',
                            color: 'white'
                        }}
                    >
                        <Typography variant="h5" component="h2" gutterBottom>
                            Contact Information
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3, color: alpha('#fff', 0.9) }}>
                            If you have questions, concerns, or wish to exercise your data rights, please contact:
                        </Typography>

                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                bgcolor: alpha('#fff', 0.1),
                                backdropFilter: 'blur(10px)',
                                borderRadius: 2
                            }}
                        >
                            <Typography variant="h6" gutterBottom sx={{ color: alpha('#fffdfdff', 0.9) }}>
                                24Care Hub Center LLC

                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <MapPin sx={{ fontSize: 20, color: alpha('#fff', 0.7) }} />
                                <Typography variant="body2" sx={{ color: alpha('#fffdfdff', 0.9) }}>Al-Ain, Abu Dhabi, UAE</Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Mail sx={{ fontSize: 20, color: alpha('#fff', 0.7) }} />
                                <a href="mailto:info@24care.ae" style={{ color: 'white', textDecoration: 'underline' }}>
                                    <Typography variant="body2">info@24care.ae</Typography>
                                </a>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Phone sx={{ fontSize: 20, color: alpha('#fff', 0.7) }} />
                                <a href="tel:+97137221188" style={{ color: 'white', textDecoration: 'underline' }}>
                                    <Typography variant="body2">+971 3 7221188</Typography>
                                </a>
                            </Box>
                        </Paper>
                    </Paper>

                    {/* Footer */}
                    <Box sx={{ textAlign: 'center', mt: 6, py: 4, borderTop: 1, borderColor: 'grey.200' }}>
                        <Typography variant="body2" color="text.secondary">
                            Last updated: {new Date().toLocaleDateString()}. We may update this policy periodically.
                        </Typography>
                    </Box>
                </Container>
            </Box>
        </ThemeProvider>
    );
};

export default PrivacyPolicy;